import React from 'react';
import { CirclePicker } from 'react-color';
import {
  Box,
  Button,
  Group,
  NavLink,
  Paper,
  Popover,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconDeviceGamepad,
  IconHelp,
  IconInfoCircle,
  IconLock,
  IconMap,
  IconQrcode,
  IconShare,
  IconSparkles,
} from '@tabler/icons-react';

import Config from '../../Config.jsx';
import Localization from '../../Localization.jsx';
import LocaleDetailView from './LocaleDetailView.jsx';
import QRCodeDialog from './QRCodeDialog.jsx';

const CATEGORY_BASIC = 'basic';
const CATEGORY_LOCATION = 'location';
const CATEGORY_PUBLISHING = 'publishing';
const CATEGORY_UNLOCK = 'unlock';
const CATEGORY_GAME = 'game';
const CATEGORY_AI = 'ai';

const CATEGORY_META = {
  [CATEGORY_BASIC]: {
    label: () => Localization.strings.DETAILS,
    icon: IconInfoCircle,
  },
  [CATEGORY_LOCATION]: {
    label: () => Localization.strings.LOCATION,
    icon: IconMap,
  },
  [CATEGORY_PUBLISHING]: {
    label: () => Localization.strings.LOCALE_SETTINGS_PUBLISHING,
    icon: IconShare,
  },
  [CATEGORY_UNLOCK]: {
    label: () => Localization.strings.LOCALE_SETTINGS_HOTSPOT_UNLOCK,
    icon: IconLock,
  },
  [CATEGORY_GAME]: {
    label: () => Localization.strings.LOCALE_SETTINGS_GAME_MODE,
    icon: IconDeviceGamepad,
  },
  [CATEGORY_AI]: {
    label: () => Localization.strings.LOCALE_SETTINGS_AI_FEATURES,
    icon: IconSparkles,
  },
};

class EditLocaleSettingsPanel extends LocaleDetailView {
  constructor(props) {
    super(props);
    this.state.settingsCategory = CATEGORY_BASIC;
    this.handleCategoryChange = this.handleCategoryChange.bind(this);
    this.updateBooleanSetting = this.updateBooleanSetting.bind(this);
  }

  handleCategoryChange(category) {
    let activeView = 2;
    if (category === CATEGORY_BASIC) {
      activeView = 0;
    } else if (category === CATEGORY_LOCATION) {
      activeView = 1;
    }

    this.changeActiveView(activeView);
    this.setState({ settingsCategory: category });
    this.state.settingsCategory = category;
  }

  updateSetting(field, value) {
    this.setState({ [field]: value, isDirty: true });
    this.state[field] = value;
    this.state.isDirty = true;
  }

  updateBooleanSetting(field, checked) {
    this.updateSetting(field, Boolean(checked));
  }

  markDirty() {
    this.setState({ isDirty: true });
    this.state.isDirty = true;
  }

  checkDirty(oldValue, newValue) {
    if (oldValue != newValue) {
      this.markDirty();
    }
  }

  onUploadSuccess(res) {
    super.onUploadSuccess(res);
    this.markDirty();
  }

  onAbortUpload() {
    super.onAbortUpload();
    this.markDirty();
  }

  onBoundsChanged() {
    super.onBoundsChanged();
    this.markDirty();
  }

  handleColorChange(color) {
    this.localeColorChanged(color);
    this.markDirty();
    this.handleColorPickerClose();
  }

  validateInput() {
    const originalView = this.state.activeView;
    const checks = [
      { view: 0, category: CATEGORY_BASIC },
      { view: 1, category: CATEGORY_LOCATION },
      { view: 2, category: CATEGORY_UNLOCK },
    ];

    for (const check of checks) {
      this.state.activeView = check.view;
      if (!super.validateInput()) {
        this.handleCategoryChange(check.category);
        return false;
      }
    }

    this.state.activeView = originalView;
    return true;
  }

  renderHelpIcon(message) {
    if (!message) {
      return null;
    }

    return (
      <Tooltip label={message} withArrow multiline w={320}>
        <span style={{ display: 'inline-flex', cursor: 'help' }}>
          <IconHelp size={18} style={{ color: 'var(--mantine-color-gray-5)' }} />
        </span>
      </Tooltip>
    );
  }

  renderWarningIcon(message) {
    return (
      <Tooltip label={message} withArrow multiline w={320}>
        <span style={{ display: 'inline-flex', cursor: 'help' }}>
          <IconAlertTriangle
            size={18}
            style={{ color: 'var(--mantine-color-yellow-6)' }}
          />
        </span>
      </Tooltip>
    );
  }

  renderSettingRow({ label, description, control, warning }) {
    return (
      <Paper withBorder radius="md" p="md">
        <Group justify="space-between" align="center" wrap="nowrap" gap="xl">
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text fw={600}>{label}</Text>
              {warning ? this.renderWarningIcon(warning) : null}
              {description ? this.renderHelpIcon(description) : null}
            </Group>
            {description && (
              <Text size="sm" c="dimmed" mt={3} lineClamp={2}>
                {description}
              </Text>
            )}
          </Box>
          <Box style={{ flexShrink: 0 }}>
            {control}
          </Box>
        </Group>
      </Paper>
    );
  }

  renderToggleRow({ field, label, description, warning, disabled = false }) {
    return this.renderSettingRow({
      label,
      description,
      warning,
      control: (
        <Switch
          checked={Boolean(this.state[field])}
          onChange={(event) => this.updateBooleanSetting(field, event.currentTarget.checked)}
          disabled={disabled}
          color="brand"
          size="md"
          aria-label={label}
        />
      ),
    });
  }

  renderBasicCategory() {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" p="lg">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
            <Select
              label={Localization.strings.LANGUAGE}
              value={this.state.localeLanguage}
              onChange={(value) => this.updateSetting('localeLanguage', value)}
              data={[
                { value: 'tc', label: '繁體中文' },
                { value: 'sc', label: '简体中文' },
                { value: 'en', label: 'English' },
              ]}
              allowDeselect={false}
            />
            <div>
              <Text size="sm" fw={500} mb={4}>{Localization.strings.COLOR}</Text>
              <Group gap="xs" wrap="nowrap">
                <Popover
                  opened={Boolean(this.state.colorPickerAnchor)}
                  onChange={(opened) => { if (!opened) this.handleColorPickerClose(); }}
                  position="bottom-start"
                >
                  <Popover.Target>
                    <Button
                      variant="default"
                      onClick={this.handleColorPickerClick}
                      style={{ width: '100%', justifyContent: 'flex-start' }}
                    >
                      <span
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          backgroundColor: `#${this.state.localeColor}`,
                          border: '1px solid var(--mantine-color-gray-4)',
                        }}
                      />
                    </Button>
                  </Popover.Target>
                  <Popover.Dropdown>
                    <CirclePicker
                      color={this.state.localeColor}
                      onChange={(color) => this.handleColorChange(color)}
                    />
                  </Popover.Dropdown>
                </Popover>
                {this.renderHelpIcon(Localization.strings.LOCALE_COLOR_REMINDER)}
              </Group>
            </div>
          </SimpleGrid>
        </Paper>

        <Paper withBorder radius="md" p="lg">
          {super.render()}
        </Paper>
      </Stack>
    );
  }

  renderLocationCategory() {
    return (
      <Paper withBorder radius="md" p="md">
        {super.render()}
      </Paper>
    );
  }

  renderPublishingCategory() {
    const duplicateSharingDisabled = Boolean(this.props.isDuplicate);

    return (
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {this.renderToggleRow({
            field: 'localeShareable',
            label: Localization.strings.SHARABLE,
            description: duplicateSharingDisabled
              ? Localization.strings.DUPLICATION_NOT_ALLOWED_HINT
              : Localization.strings.SHAREABLE_HINT,
            disabled: duplicateSharingDisabled,
          })}
          {this.renderToggleRow({
            field: 'localePublish',
            label: Localization.strings.PUBLISH,
            description: Localization.strings.PUBLISH_HINT,
          })}
        </SimpleGrid>
        {this.renderSettingRow({
          label: Localization.strings.DUPLICATION_RULE,
          description: Localization.strings.DUPLICATION_RULE_HINT,
          control: (
            <Select
              value={String(this.state.localeDuplicationAllowed)}
              onChange={(value) => this.updateSetting('localeDuplicationAllowed', Number(value))}
              data={[
                { value: '0', label: Localization.strings.DUPLICATION_RULE_0 },
                { value: '1', label: Localization.strings.DUPLICATION_RULE_1 },
                { value: '2', label: Localization.strings.DUPLICATION_RULE_2 },
                { value: '3', label: Localization.strings.DUPLICATION_RULE_3 },
              ]}
              allowDeselect={false}
              w={320}
            />
          ),
        })}
        {this.props.root.getCurrentLocale() && this.renderSettingRow({
          label: Localization.strings.QR_CODE,
          description: Localization.strings.QR_CODE_DESC,
          control: (
            <Button
              variant="light"
              color="brand"
              leftSection={<IconQrcode size={18} />}
              onClick={() => this.requestQRCode()}
            >
              {Localization.strings.QR_CODE}
            </Button>
          ),
        })}
      </Stack>
    );
  }

  renderUnlockCategory() {
    return (
      <Stack gap="md">
        {this.renderToggleRow({
          field: 'hsRevealCodeEnabled',
          label: Localization.strings.USE_REVEAL_CODE,
          description: Localization.strings.REVEAL_CODE_HINT,
          warning: Localization.strings.NEW_APP_VERSION_REQUIRED,
        })}
        {this.state.hsRevealCodeEnabled && (
          <Paper withBorder radius="md" p="md">
            <TextInput
              label={Localization.strings.REVEAL_CODE}
              description={Localization.strings.REVEAL_CODE_REQUIREMENT_1}
              value={this.state.hsRevealCode}
              onChange={(event) => this.updateSetting('hsRevealCode', event.target.value)}
              maxLength={Config.MAX_TEXT_LEN().REVEAL_CODE}
              maw={420}
            />
          </Paper>
        )}
      </Stack>
    );
  }

  renderGameCategory() {
    return (
      <Stack gap="md">
        {this.renderToggleRow({
          field: 'moodEnabled',
          label: Localization.strings.MOOD_ENABLED,
          description: Localization.strings.MOOD_ENABLED_HELP,
          warning: Localization.strings.AVATAR_INTERACTION_APP_VERSION_REQUIRED,
        })}
        {this.renderToggleRow({
          field: 'nudgeEnabled',
          label: Localization.strings.NUDGE_ENABLED,
          description: Localization.strings.NUDGE_ENABLED_HELP,
        })}
        {this.renderToggleRow({
          field: 'chatTeacherToStudent',
          label: Localization.strings.CHAT_TEACHER_TO_STUDENT,
          description: Localization.strings.CHAT_TEACHER_TO_STUDENT_HELP,
        })}
        {this.renderToggleRow({
          field: 'chatStudentToStudent',
          label: Localization.strings.CHAT_STUDENT_TO_STUDENT,
          description: Localization.strings.CHAT_STUDENT_TO_STUDENT_HELP,
        })}
        {this.renderToggleRow({
          field: 'chatStudentToTeacher',
          label: Localization.strings.CHAT_STUDENT_TO_TEACHER,
          description: Localization.strings.CHAT_STUDENT_TO_TEACHER_HELP,
        })}
      </Stack>
    );
  }

  renderAiCategory() {
    return (
      <Stack gap="md">
        {this.renderSettingRow({
          label: Localization.strings.AI_ASSESSMENT,
          description: Localization.strings.AI_ASSESSMENT_MODE_HELP,
          control: (
            <SegmentedControl
              value={String(this.state.manualAiAssessment)}
              onChange={(value) => this.updateSetting('manualAiAssessment', Number(value))}
              data={[
                { value: '1', label: Localization.strings.MANUALLY },
                { value: '0', label: Localization.strings.AUTOMATICALLY },
              ]}
              color="brand"
            />
          ),
        })}
        {this.renderSettingRow({
          label: Localization.strings.AUTO_SHARE_ASSESSMENT,
          description: Localization.strings.AUTO_SHARE_ASSESSMENT_HELP,
          control: (
            <Select
              value={String(this.state.autoShareAssessment)}
              onChange={(value) => this.updateSetting('autoShareAssessment', Number(value))}
              data={[
                { value: '0', label: Localization.strings.NO },
                { value: '1', label: Localization.strings.AUTO_SHARE_VIA_DEVICE },
                { value: '2', label: Localization.strings.AUTO_SHARE_VIA_EDCITY },
                { value: '3', label: Localization.strings.AUTO_SHARE_VIA_BOTH },
              ]}
              allowDeselect={false}
              w={380}
            />
          ),
        })}
      </Stack>
    );
  }

  renderCategoryContent() {
    switch (this.state.settingsCategory) {
      case CATEGORY_LOCATION:
        return this.renderLocationCategory();
      case CATEGORY_PUBLISHING:
        return this.renderPublishingCategory();
      case CATEGORY_UNLOCK:
        return this.renderUnlockCategory();
      case CATEGORY_GAME:
        return this.renderGameCategory();
      case CATEGORY_AI:
        return this.renderAiCategory();
      case CATEGORY_BASIC:
      default:
        return this.renderBasicCategory();
    }
  }

  render() {
    return (
      <div style={{ height: '100%', minHeight: 0, display: 'flex' }}>
        <Box
          component="nav"
          style={{
            width: 230,
            flexShrink: 0,
            padding: 12,
            borderRight: '1px solid var(--mantine-color-gray-3)',
            background: 'var(--mantine-color-gray-0)',
          }}
        >
          <Stack gap={4}>
            {Object.entries(CATEGORY_META).map(([key, meta]) => {
              const CategoryIcon = meta.icon;
              return (
                <NavLink
                  key={key}
                  active={this.state.settingsCategory === key}
                  label={meta.label()}
                  leftSection={<CategoryIcon size={19} />}
                  onClick={() => this.handleCategoryChange(key)}
                  color="brand"
                  variant="light"
                  style={{ borderRadius: 8 }}
                />
              );
            })}
          </Stack>
        </Box>

        <Box style={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', padding: 24 }}>
          {this.renderCategoryContent()}
        </Box>

        <QRCodeDialog
          open={this.state.qrDialogOpen}
          onClose={() => this.setState({ qrDialogOpen: false })}
          localeId={this.props.root.getCurrentLocale()?.id}
          localeName={this.props.root.getCurrentLocale()?.name}
          isPublished={parseInt(this.props.root.getCurrentLocale()?.is_ready, 10) > 0}
          pendingUnpublish={parseInt(this.props.root.getCurrentLocale()?.is_ready, 10) > 0
            && !this.state.localePublish}
        />
      </div>
    );
  }
}

export default EditLocaleSettingsPanel;
